import { Badge } from '@/components/ui/badge'
import * as Combobox from '@/components/ui/combobox'
import * as Field from '@/components/ui/field'
import { IconButton } from '@/components/ui/icon-button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import { createListCollection } from '@ark-ui/solid'
import { debounce } from '@solid-primitives/scheduled'
import { createQuery } from '@tanstack/solid-query'
import { CheckIcon, ChevronsUpDownIcon, XIcon } from 'lucide-solid'
import { For, Show, createMemo, createSignal } from 'solid-js'
import { Box } from 'styled-system/jsx'

type Column = 'symbol' | 'exchange'

const searchValues = async (column: Column, query: string) => {
  if (!query) return []

  const { data, error } = await api()
    .from('setup')
    .select(column)
    .ilike(column, `%${query}%`)
    .not(column, 'is', null)
    .limit(50)

  if (error) {
    console.error(`[${column} search] ❌ Supabase fetch error:`, error)
    return []
  }

  // Many setups share a symbol/exchange, so de-duplicate before showing them
  const unique = [...new Set(data.map((row) => (row as Record<Column, string>)[column]).filter(Boolean))]
  return unique.slice(0, 10).map((value) => ({ label: value, value }))
}

/** Multi-select combobox over the distinct values of a text column on `setup` */
export function ValuesCombobox(props: {
  column: Column
  label: string
  placeholder: string
  selected: string[]
  onChange: (value: string[]) => void
}) {
  const [inputValue, setInputValue] = createSignal('')

  const debouncedSetInputValue = debounce((val: string) => {
    setInputValue(val)
  }, 300)

  const query = createQuery(() => ({
    queryKey: ['setup-values', props.column, inputValue()],
    queryFn: () => searchValues(props.column, inputValue()),
    // Keeps the previous data on screen while fetching the new data
    // to prevent the dropdown list from flashing empty
    placeholderData: (prev) => prev,
  }))

  const collection = createMemo(() =>
    createListCollection({
      items: query.data || [],
    }),
  )

  const removeValue = (val: string) => {
    props.onChange(props.selected.filter((v) => v !== val))
  }

  return (
    <Field.Root>
      <Combobox.Root
        width="full"
        onInputValueChange={(value) => {
          debouncedSetInputValue(value.inputValue)
        }}
        value={props.selected}
        onValueChange={({ value }) => {
          props.onChange(value)
        }}
        collection={collection()}
        multiple
      >
        <Combobox.Label>{props.label}</Combobox.Label>
        <Combobox.Control display="grid" gridTemplateColumns="1fr auto" gap="1">
          <Combobox.Input
            placeholder={props.placeholder}
            asChild={(inputProps) => <Input {...inputProps()} />}
          />
          <Combobox.Trigger
            asChild={(triggerProps) => (
              <IconButton
                variant="plain"
                aria-label="open"
                size="md"
                {...triggerProps()}
              >
                <Show when={query.isFetching} fallback={<ChevronsUpDownIcon />}>
                  <Spinner />
                </Show>
              </IconButton>
            )}
          />
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.ItemGroup>
              <Show when={query.isFetching && (!query.data || query.data.length === 0)}>
                <Box py="2" display="flex" justifyContent="center">
                  <Spinner />
                </Box>
              </Show>

              <Show when={!query.isFetching && query.data?.length === 0 && inputValue() !== ''}>
                <Box py="2" px="3">
                  <Text textStyle="sm" color="fg.subtle">No matches found</Text>
                </Box>
              </Show>

              <For each={query.data}>
                {(item) => (
                  <Combobox.Item item={item}>
                    <Combobox.ItemText>{item.label}</Combobox.ItemText>
                    <Combobox.ItemIndicator>
                      <CheckIcon />
                    </Combobox.ItemIndicator>
                  </Combobox.Item>
                )}
              </For>
            </Combobox.ItemGroup>
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>

      <Show when={props.selected.length > 0}>
        <Box mt="2" display="flex" flexWrap="wrap" gap="2">
          <For each={props.selected}>
            {(val) => (
              <Badge>
                {val}
                <IconButton
                  aria-label={`Remove ${val}`}
                  variant="plain"
                  height="3"
                  width="3"
                  onClick={() => removeValue(val)}
                >
                  <XIcon />
                </IconButton>
              </Badge>
            )}
          </For>
        </Box>
      </Show>
    </Field.Root>
  )
}
